import React, { useEffect, useMemo, useState } from "react";
import { ExamAulasDoc, getExamAulasCatalog } from "../services/aulasService";

interface AulasPageProps {
  onBack: () => void;
  initialExamId?: string | null;
  initialSubject?: string | null;
  initialTopic?: string | null;
}

const normalize = (v: string) =>
  String(v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const AulasPage: React.FC<AulasPageProps> = ({ onBack, initialExamId, initialSubject, initialTopic }) => {
  const [catalog, setCatalog] = useState<ExamAulasDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExamId, setSelectedExamId] = useState<string | null>(initialExamId || null);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const data = await getExamAulasCatalog();
        setCatalog(data);
        if (!selectedExamId) {
          const firstActive = data.find((d) => d.status === "ativo");
          if (initialExamId) setSelectedExamId(initialExamId);
          else if (firstActive) setSelectedExamId(firstActive.id);
        }
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const selectedExam = useMemo(
    () => catalog.find((exam) => exam.id === selectedExamId) || null,
    [catalog, selectedExamId],
  );

  const examSubjects = useMemo(() => {
    if (!selectedExam) return [];
    if (!initialSubject) return selectedExam.subjects || [];
    return (selectedExam.subjects || []).filter((s) => normalize(s.subject).includes(normalize(initialSubject)));
  }, [selectedExam, initialSubject]);

  if (loading) {
    return <div className="min-h-screen bg-white p-8 text-gray-500">Carregando aulas...</div>;
  }

  if (!selectedExam) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <button onClick={onBack} className="mb-6 px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700">← Voltar</button>
          <h1 className="text-2xl font-black text-slate-900">Aulas</h1>
          <p className="text-slate-600 mt-2">Nenhum concurso com aulas encontrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={onBack} className="mb-6 px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700">← Voltar</button>

        <header className="mb-6">
          <h1 className="text-3xl font-black text-slate-900">Aulas</h1>
          <p className="text-slate-600 mt-1">Concurso: <strong>{selectedExam.name}</strong></p>
          {(initialSubject || initialTopic) && (
            <p className="text-sm text-cyan-700 mt-1">
              Filtro aplicado: {initialSubject || "Matéria"}{initialTopic ? ` • ${initialTopic}` : ""}
            </p>
          )}
        </header>

        <div className="mb-6 overflow-x-auto flex gap-2">
          {catalog.map((exam) => (
            <button
              key={exam.id}
              onClick={() => setSelectedExamId(exam.id)}
              className={`px-3 py-2 rounded-lg text-sm font-bold whitespace-nowrap ${exam.id === selectedExam.id ? "bg-cyan-600 text-white" : "bg-white border border-slate-200 text-slate-700"}`}
            >
              {exam.name}
            </button>
          ))}
        </div>

        <div className="space-y-6">
          {examSubjects.map((subject) => (
            <section key={subject.subject} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-xl font-extrabold text-slate-900 mb-4">{subject.subject}</h2>

              <div className="space-y-3">
                {(subject.topics || [])
                  .filter((topic) => !initialTopic || normalize(topic.topic).includes(normalize(initialTopic)))
                  .map((topic) => (
                  <div key={topic.topic} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <h3 className="font-bold text-slate-800 mb-2">{topic.topic}</h3>
                    <div className="space-y-2">
                      {(topic.aulas || []).map((aula, idx) => (
                        <a
                          key={`${topic.topic}-${idx}`}
                          href={aula.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded-lg border border-emerald-100 bg-white hover:border-emerald-300 p-3"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-semibold text-emerald-700">{aula.title}</p>
                            <span className="text-xs font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">GRÁTIS</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {(aula.platform || "youtube").toUpperCase()} • {aula.duration || "20-45 min"} • {aula.teacher || "Curadoria"}
                          </p>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AulasPage;
